import { Api } from "../../../../common/service/Api";
import { UploadVendorAgreementResponse } from "../../../../common/service/server/controller/StorageController";
import { OrganizationDataRecipientDto } from "../../../../common/service/server/dto/OrganizationDataRecipientDto";
import { OrganizationDto } from "../../../../common/service/server/dto/OrganizationDto";
import { DisclosureState } from "../../../components/organisms/data-recipients/DataRecipientDisclosureSettings";
import { UpdateVendorArgs } from "../components/workflows/create-data-recipient/ClassificationForDataRecipientPage";

export const saveServiceProviderDocumentation = async (
  org: OrganizationDto,
  vendor: OrganizationDataRecipientDto,
  fileName: string,
  file: File,
  state: DisclosureState,
  updateVendor: (v: UpdateVendorArgs) => Promise<void>,
) => {
  let updatedVendor: OrganizationDataRecipientDto = { ...vendor };

  // Handle file upload if there's a new file
  let agreementResponse: UploadVendorAgreementResponse | null = null;
  if (file && fileName != vendor.tosFileName) {
    agreementResponse = await Api.storage.postOrganizationVendorAgreement(
      org.id,
      vendor.vendorId,
      file.name,
      file,
    );

    if (agreementResponse) {
      updatedVendor.tosFileName = agreementResponse.fileName;
      updatedVendor.tosFileKey = agreementResponse.fileKey;
      // TODO(saurabh): this property can be added to OrganizationDataRecipientDto
      // updatedVendor.vendorContractReviewedId = null;
    }
  }

  // Handle file deletion if marked for delete
  if (fileName === "DELETE_" && vendor.tosFileName) {
    // DELETE: Remove existing service provider agreement
    await Api.storage.deleteOrganizationVendorAgreement(org.id, vendor.vendorId, vendor.tosFileKey);

    updatedVendor.tosFileName = "DELETE_";
    updatedVendor.tosFileKey = null;
  }

  updatedVendor.serviceProviderInfoRequestSentDate = "1970-01-01T00:00:00Z";

  await updateVendor({
    vendorId: vendor.vendorId,
    vendorSettings: {
      ...updatedVendor,
    },
  });
};

export const saveGdprDocumentation = async (
  org: OrganizationDto,
  vendor: OrganizationDataRecipientDto,
  fileName: string,
  file: File,
  state: DisclosureState,
  updateVendor: (v: UpdateVendorArgs) => Promise<void>,
) => {
  let updatedVendor: OrganizationDataRecipientDto = { ...vendor };

  let processorAgreementResponse: UploadVendorAgreementResponse | null = null;
  const isNewProcessorFile =
    vendor.gdprProcessorSetting === "Processor" &&
    fileName != vendor.gdprProcessorGuaranteeFileName;
  const isNewControllerFile =
    vendor.gdprProcessorSetting === "Controller" &&
    fileName != vendor.gdprControllerGuaranteeFileName;
  if (file && (isNewProcessorFile || isNewControllerFile)) {
    processorAgreementResponse = await Api.storage.postOrganizationVendorAgreement(
      org.id,
      vendor.vendorId,
      file.name,
      file,
    );

    // TODO(saurabh): how to handle if `processorAgreementResponse` is null?
    // for now, just reset the file names to the existing files
    if (vendor.gdprProcessorSetting === "Processor") {
      updatedVendor.gdprProcessorGuaranteeFileName =
        processorAgreementResponse.fileName ?? vendor.gdprProcessorGuaranteeFileName;
      updatedVendor.gdprProcessorGuaranteeFileKey =
        processorAgreementResponse.fileKey ?? vendor.gdprProcessorGuaranteeFileKey;
    } else if (vendor.gdprProcessorSetting === "Controller") {
      updatedVendor.gdprControllerGuaranteeFileName =
        processorAgreementResponse.fileName ?? vendor.gdprControllerGuaranteeFileName;
      updatedVendor.gdprControllerGuaranteeFileKey =
        processorAgreementResponse.fileKey ?? vendor.gdprControllerGuaranteeFileKey;
    }
  }

  const hasProcessorFile =
    vendor.gdprProcessorSetting === "Processor" && Boolean(vendor.gdprProcessorGuaranteeFileKey);
  const hasControllerFile =
    vendor.gdprProcessorSetting === "Controller" && Boolean(vendor.gdprControllerGuaranteeFileKey);

  if (fileName === "DELETE_" && hasProcessorFile) {
    await Api.storage.deleteOrganizationVendorAgreement(
      org.id,
      vendor.vendorId,
      vendor.gdprProcessorGuaranteeFileKey,
    );

    updatedVendor.gdprProcessorGuaranteeFileName = "DELETE_";
    updatedVendor.gdprProcessorGuaranteeFileKey = null;
  }

  if (fileName === "DELETE_" && hasControllerFile) {
    await Api.storage.deleteOrganizationVendorAgreement(
      org.id,
      vendor.vendorId,
      vendor.gdprControllerGuaranteeFileKey,
    );

    updatedVendor.gdprControllerGuaranteeFileName = "DELETE_";
    updatedVendor.gdprControllerGuaranteeFileKey = null;
  }

  await updateVendor({
    vendorId: vendor.vendorId,
    vendorSettings: updatedVendor,
  });
};

export const saveSccDocumentation = async (
  org: OrganizationDto,
  vendor: OrganizationDataRecipientDto,
  fileName: string,
  file: File,
  state: DisclosureState,
  updateVendor: (v: UpdateVendorArgs) => Promise<void>,
) => {
  let updatedVendor: OrganizationDataRecipientDto = { ...vendor };

  let sccDocumentationResponse: UploadVendorAgreementResponse | null = null;
  if (file && fileName != vendor.gdprSccFileName) {
    sccDocumentationResponse = await Api.storage.postOrganizationVendorAgreement(
      org.id,
      vendor.vendorId,
      file.name,
      state.gdprSccFile,
    );

    updatedVendor.gdprSccFileName = sccDocumentationResponse.fileName ?? vendor.gdprSccFileName;
    updatedVendor.gdprSccFileKey = sccDocumentationResponse.fileKey ?? vendor.gdprSccFileKey;
  }

  if (fileName === "DELETE_" && vendor.gdprSccFileName) {
    await Api.storage.deleteOrganizationVendorAgreement(
      org.id,
      vendor.vendorId,
      vendor.gdprSccFileKey,
    );

    updatedVendor.gdprSccFileName = "DELETE_";
    updatedVendor.gdprSccFileKey = null;
  }

  await updateVendor({
    vendorId: vendor.vendorId,
    vendorSettings: updatedVendor,
  });
};

export const saveCaContractRequirementDocumentation = async (
  org: OrganizationDto,
  vendor: OrganizationDataRecipientDto,
  fileName: string,
  file: File,
  updateVendor: (v: UpdateVendorArgs) => Promise<void>,
) => {
  let updatedVendor: OrganizationDataRecipientDto = { ...vendor };

  let caContractRequirementResponse: UploadVendorAgreementResponse | null = null;
  if (file && fileName != vendor.caContractRequirementFileName) {
    caContractRequirementResponse = await Api.storage.postOrganizationVendorAgreement(
      org.id,
      vendor.vendorId,
      file.name,
      file,
    );

    updatedVendor.caContractRequirementFileName =
      caContractRequirementResponse?.fileName ?? vendor.caContractRequirementFileName;
    updatedVendor.caContractRequirementFileKey =
      caContractRequirementResponse?.fileKey ?? vendor.caContractRequirementFileKey;
  }

  if (fileName === "DELETE_" && vendor.caContractRequirementFileName) {
    await Api.storage.deleteOrganizationVendorAgreement(
      org.id,
      vendor.vendorId,
      vendor.caContractRequirementFileKey,
    );

    updatedVendor.caContractRequirementFileName = "DELETE_";
    updatedVendor.caContractRequirementFileKey = null;
  }

  await updateVendor({
    vendorId: vendor.vendorId,
    vendorSettings: updatedVendor,
  });
};

export const saveGppSignalDocumentation = async (
  org: OrganizationDto,
  vendor: OrganizationDataRecipientDto,
  fileName: string,
  file: File,
  updateVendor: (v: UpdateVendorArgs) => Promise<void>,
) => {
  let updatedVendor: OrganizationDataRecipientDto = { ...vendor };

  let gppSignalDocumentationResponse: UploadVendorAgreementResponse | null = null;
  if (file && fileName != vendor.gppSignalDocumentationFileName) {
    gppSignalDocumentationResponse = await Api.storage.postOrganizationVendorAgreement(
      org.id,
      vendor.vendorId,
      file.name,
      file,
    );

    updatedVendor.gppSignalDocumentationFileName =
      gppSignalDocumentationResponse?.fileName ?? vendor.gppSignalDocumentationFileName;
    updatedVendor.gppSignalDocumentationFileKey =
      gppSignalDocumentationResponse?.fileKey ?? vendor.gppSignalDocumentationFileKey;
  }

  if (fileName === "DELETE_" && vendor.gppSignalDocumentationFileName) {
    await Api.storage.deleteOrganizationVendorAgreement(
      org.id,
      vendor.vendorId,
      vendor.gppSignalDocumentationFileKey,
    );

    updatedVendor.gppSignalDocumentationFileName = "DELETE_";
    updatedVendor.gppSignalDocumentationFileKey = null;
  }

  await updateVendor({
    vendorId: vendor.vendorId,
    vendorSettings: updatedVendor,
  });
};
